import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../shared/api';
import { formatMoney, formatNumber, formatDate } from '../shared/formatting';
import { PersonName } from '../shared/PersonName';
import type { Filters } from '../types/biApp';

type OfferEntry = {
    offer_id: number;
    offer_number: string;
    offer_date: string;
    amount_total: number;
    kasa_id: string;
    age_days: number | null;
    source_file: string;
    invoice_numbers: string;
    invoice_count: number;
};

type DrilldownPayload = {
    partner: {
        partner_code: string;
        partner_name: string;
        partner_name_is_person?: boolean;
        partner_name_hint?: string | null;
    };
    summary: {
        offers_count: number;
        accepted_count: number;
        rejected_count: number;
        offered_total: number;
        realized_total: number;
        conversion_count_pct: number;
        conversion_value_pct: number;
        first_offer_date: string | null;
        last_offer_date: string | null;
        kasa_breakdown: Array<{ kasa_id: string; offers_count: number }>;
    };
    accepted: OfferEntry[];
    rejected: OfferEntry[];
};

type Tab = 'summary' | 'accepted' | 'rejected' | 'ruc';

type RucOfferRow = {
    offer_id: number;
    offer_number: string;
    offer_date: string;
    offer_amount_eur: number;
    revenue_eur: number;
    cost_eur: number;
    ruc_eur: number;
    ruc_pct: number | null;
    status: string;
    linked_invoices_count: number;
    linked_supplier_orders_count: number;
};

type RucResponse = {
    partner_code: string;
    partner_name: string;
    offers_count: number;
    revenue_total: number;
    cost_total: number;
    ruc_total: number;
    ruc_pct: number | null;
    offers: RucOfferRow[];
};

/**
 * Side-drawer drilldown na pojedinog ADO klijenta.
 * Otvara se klikom na ime/red u m.1 (ADO modulu). Klijent je anonimiziran (PersonName).
 * Sadržaj: sažetak + tab s prihvaćenim ponudama (s računom) i tab s odbijenima (bez računa).
 */
export function AdoClientDrilldownDrawer({
    token, partnerCode, filters, locale, onClose,
}: {
    token: string;
    partnerCode: string;
    filters: Filters;
    locale: 'hr' | 'en';
    onClose: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<DrilldownPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tab, setTab] = useState<Tab>('summary');

    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') onClose();
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        setLoading(true);
        setError('');
        const params = new URLSearchParams();
        if (filters.dateFrom) params.set('date_from', filters.dateFrom);
        if (filters.dateTo) params.set('date_to', filters.dateTo);
        const qs = params.toString() ? `?${params.toString()}` : '';
        api<DrilldownPayload>(`/ado-m1/client/${encodeURIComponent(partnerCode)}/offers${qs}`, token)
            .then((r) => setData(r))
            .catch((e) => setError(e instanceof Error ? e.message : 'API'))
            .finally(() => setLoading(false));
    }, [token, partnerCode, filters.dateFrom, filters.dateTo]);

    const offers = useMemo<OfferEntry[]>(() => {
        if (!data) return [];
        return tab === 'accepted' ? data.accepted : (tab === 'rejected' ? data.rejected : []);
    }, [tab, data]);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(20,16,8,0.32)',
                backdropFilter: 'blur(2px)',
                zIndex: 90,
                display: 'flex',
                justifyContent: 'flex-end',
            }}
        >
            <aside
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: 'min(560px, 96vw)',
                    height: '100%',
                    background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)',
                    boxShadow: '-20px 0 60px rgba(107,79,28,0.22), -1px 0 0 rgba(var(--ad-soft-rgb),0.18)',
                    overflowY: 'auto',
                    animation: 'biDrawerSlideIn 0.18s ease-out',
                }}
            >
                <style>{`
                    @keyframes biDrawerSlideIn {
                        from { transform: translateX(20px); opacity: 0.4; }
                        to { transform: translateX(0); opacity: 1; }
                    }
                `}</style>
                <header style={{
                    background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 60%, var(--ad-acc) 100%)',
                    color: '#fffbeb',
                    padding: '16px 20px',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '0.72rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>
                            {t('Klijent', 'Client')} · {t('Modul 1 ADO', 'Module 1 ADO')}
                        </p>
                        <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.25rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                            {data ? (
                                <PersonName
                                    value={data.partner.partner_name}
                                    isPerson={data.partner.partner_name_is_person}
                                    hint={data.partner.partner_name_hint ?? undefined}
                                    style={{ color: '#fffbeb' }}
                                />
                            ) : (loading ? t('Učitavam…', 'Loading…') : '—')}
                        </h2>
                        <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'rgba(255,251,235,0.78)' }}>
                            {t('Šifra', 'Code')}: {partnerCode}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('Zatvori', 'Close')}
                        style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.7rem', cursor: 'pointer', lineHeight: 1, padding: '2px 6px' }}
                    >×</button>
                </header>

                {error ? <p className="error" style={{ padding: '0 20px' }}>{error}</p> : null}
                {loading ? <p className="muted" style={{ padding: '14px 20px' }}>{t('Učitavam…', 'Loading…')}</p> : null}

                {data ? (
                    <>
                        <div style={{ display: 'flex', gap: 6, padding: '12px 20px', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.2)' }}>
                            {([
                                { id: 'summary' as Tab, icon: '📊', label: t('Sažetak', 'Summary'), count: null },
                                { id: 'accepted' as Tab, icon: '✅', label: t('Prihvaćene', 'Accepted'), count: data.summary.accepted_count },
                                { id: 'rejected' as Tab, icon: '❌', label: t('Odbijene', 'Rejected'), count: data.summary.rejected_count },
                                { id: 'ruc' as Tab, icon: '💎', label: t('RUC', 'RUC'), count: null },
                            ]).map((tabCfg) => {
                                const active = tab === tabCfg.id;
                                return (
                                    <button
                                        key={tabCfg.id}
                                        type="button"
                                        onClick={() => setTab(tabCfg.id)}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: 8,
                                            border: active ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                                            background: active ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                                            color: active ? '#fffbeb' : 'var(--ad-ink)',
                                            fontWeight: 600,
                                            fontSize: '0.84rem',
                                            cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: 6,
                                        }}
                                    >
                                        <span>{tabCfg.icon}</span>
                                        <span>{tabCfg.label}</span>
                                        {tabCfg.count !== null ? <span style={{ opacity: 0.75 }}>({tabCfg.count})</span> : null}
                                    </button>
                                );
                            })}
                        </div>

                        <div style={{ padding: '16px 20px' }}>
                            {tab === 'summary' ? <SummaryTab summary={data.summary} locale={locale} /> : null}
                            {tab === 'accepted' || tab === 'rejected' ? (
                                <OffersTabList offers={offers} locale={locale} kind={tab} />
                            ) : null}
                            {tab === 'ruc' ? <RucTabPanel token={token} partnerCode={partnerCode} locale={locale} /> : null}
                        </div>
                    </>
                ) : null}
            </aside>
        </div>
    );
}

function SummaryTab({ summary, locale }: { summary: DrilldownPayload['summary']; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 18 }}>
                <Metric label={t('Ukupno ponuda', 'Total offers')} value={formatNumber(summary.offers_count, locale)} />
                <Metric label={t('Konverzija (kom.)', 'Conversion (count)')} value={`${summary.conversion_count_pct.toFixed(1)}%`} />
                <Metric label={t('Ponuđeno', 'Offered')} value={formatMoney(summary.offered_total, locale)} />
                <Metric label={t('Realizirano', 'Realized')} value={formatMoney(summary.realized_total, locale)} />
                <Metric label={t('Konverzija (vrij.)', 'Conversion (value)')} value={`${summary.conversion_value_pct.toFixed(1)}%`} />
                <Metric label={t('Posljednja ponuda', 'Last offer')} value={summary.last_offer_date ? formatDate(summary.last_offer_date, locale) : '—'} />
            </div>

            {summary.kasa_breakdown.length > 0 ? (
                <div>
                    <h3 style={{ margin: '0 0 8px', fontSize: '0.74rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                        {t('Po poslovnici (kasi)', 'By branch (POS)')}
                    </h3>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {summary.kasa_breakdown.map((k) => (
                            <li key={k.kasa_id} style={{
                                display: 'flex', justifyContent: 'space-between',
                                padding: '6px 10px',
                                background: 'rgba(var(--ad-acc-rgb),0.08)',
                                border: '1px solid rgba(var(--ad-soft-rgb),0.2)',
                                borderRadius: 8,
                                fontSize: '0.86rem',
                            }}>
                                <span>🏪 {t('Kasa', 'POS')} <strong>{k.kasa_id}</strong></span>
                                <span className="muted">{k.offers_count} {t('ponuda', 'offers')}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}

function Metric({ label, value }: { label: string; value: string }) {
    return (
        <div style={{
            background: '#fff',
            border: '1px solid rgba(var(--ad-soft-rgb),0.25)',
            borderRadius: 10,
            padding: '10px 12px',
        }}>
            <div style={{ fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ad-acc-3)', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#34383d' }}>{value}</div>
        </div>
    );
}

function OffersTabList({ offers, locale, kind }: { offers: OfferEntry[]; locale: 'hr' | 'en'; kind: 'accepted' | 'rejected' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    if (offers.length === 0) {
        return (
            <p className="muted" style={{ fontStyle: 'italic', textAlign: 'center', padding: '1.5rem' }}>
                {kind === 'accepted'
                    ? t('Klijent nema niti jednu prihvaćenu ponudu u odabranom periodu.', 'No accepted offers for this client in the selected period.')
                    : t('Klijent nema niti jednu odbijenu/otvorenu ponudu u odabranom periodu.', 'No rejected/open offers for this client in the selected period.')}
            </p>
        );
    }

    return (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {offers.map((o) => (
                <li key={o.offer_id} style={{
                    border: '1px solid rgba(var(--ad-soft-rgb),0.25)',
                    borderRadius: 10,
                    padding: '10px 12px',
                    background: '#fff',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                        <strong style={{ color: 'var(--ad-ink)' }}>{o.offer_number}</strong>
                        <span style={{ fontWeight: 700, color: '#34383d' }}>{formatMoney(o.amount_total, locale)}</span>
                    </div>
                    <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span>📅 {formatDate(o.offer_date, locale)}</span>
                        {o.kasa_id ? <span>🏪 {t('Kasa', 'POS')} {o.kasa_id}</span> : null}
                        {o.age_days != null ? <span>⏱ {o.age_days} {t('dana', 'days')}</span> : null}
                    </div>
                    {kind === 'accepted' && o.invoice_numbers ? (
                        <div style={{ fontSize: '0.78rem', color: '#1f7a36', marginTop: 4 }}>
                            🧾 {t('Račun(i)', 'Invoice(s)')}: {o.invoice_numbers}
                        </div>
                    ) : null}
                    {o.source_file ? (
                        <div className="muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
                            📄 {o.source_file}
                        </div>
                    ) : null}
                </li>
            ))}
        </ul>
    );
}

function RucTabPanel({ token, partnerCode, locale }: { token: string; partnerCode: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<RucResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        setLoading(true);
        api<RucResponse>(`/ado-m1/client/${encodeURIComponent(partnerCode)}/ruc`, token)
            .then(setData)
            .catch((e) => setError(e instanceof Error ? e.message : 'API'))
            .finally(() => setLoading(false));
    }, [token, partnerCode]);

    if (loading) return <p className="muted">{t('Računam…', 'Calculating…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    const statusLabel = (s: string): { label: string; color: string } => {
        switch (s) {
            case 'healthy': return { label: t('Zdravo', 'Healthy'), color: '#1f7a36' };
            case 'thin': return { label: t('Tanko (<5%)', 'Thin (<5%)'), color: 'var(--ad-acc-3)' };
            case 'loss': return { label: t('Gubitak', 'Loss'), color: '#b14040' };
            case 'cost_only': return { label: t('Samo trošak', 'Cost only'), color: 'var(--ad-acc-3)' };
            case 'revenue_only': return { label: t('Samo prihod', 'Revenue only'), color: '#34383d' };
            default: return { label: t('Otvoreno', 'Open'), color: '#888' };
        }
    };

    return (
        <>
            <div style={{
                background: 'linear-gradient(135deg, rgba(var(--ad-acc-rgb),0.1), rgba(107,79,28,0.05))',
                border: '1px solid rgba(var(--ad-soft-rgb),0.3)',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 14,
            }}>
                <strong style={{ color: 'var(--ad-ink)', fontSize: '0.92rem' }}>
                    💎 {t('RUC sažetak', 'RUC summary')}
                </strong>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginTop: 8 }}>
                    <div>
                        <div className="muted" style={{ fontSize: '0.72rem' }}>{t('Prihod', 'Revenue')}</div>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f7a36' }}>{formatMoney(data.revenue_total, locale)}</div>
                    </div>
                    <div>
                        <div className="muted" style={{ fontSize: '0.72rem' }}>{t('Trošak nabave', 'Procurement cost')}</div>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#b14040' }}>{formatMoney(data.cost_total, locale)}</div>
                    </div>
                    <div>
                        <div className="muted" style={{ fontSize: '0.72rem' }}>{t('RUC EUR', 'RUC EUR')}</div>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: data.ruc_total >= 0 ? '#1f7a36' : '#b14040' }}>{formatMoney(data.ruc_total, locale)}</div>
                    </div>
                    <div>
                        <div className="muted" style={{ fontSize: '0.72rem' }}>{t('RUC %', 'RUC %')}</div>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: (data.ruc_pct ?? 0) >= 0 ? '#1f7a36' : '#b14040' }}>{data.ruc_pct !== null ? `${data.ruc_pct.toFixed(2)}%` : '—'}</div>
                    </div>
                </div>
            </div>

            <p className="muted" style={{ fontSize: '0.78rem', margin: '0 0 8px' }}>
                {t(
                    'Lanac: ponuda → IRA (prihod) → narudžba dobavljaču zlata (trošak). Auto-alokacija pokušava povezati pri commit narudžbe iz Plannera.',
                    'Chain: offer → outgoing invoice (revenue) → gold supplier order (cost). Auto-allocation happens at planner commit time.',
                )}
            </p>

            {data.offers.length === 0 ? (
                <p className="muted" style={{ fontStyle: 'italic', padding: '1rem', textAlign: 'center' }}>
                    {t('Ovaj klijent nema ponuda u ADO-u.', 'This client has no ADO offers.')}
                </p>
            ) : (
                <div className="table-wrap">
                    <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                        <thead>
                            <tr>
                                <th>{t('Ponuda', 'Offer')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Prihod (IRA)', 'Revenue (IRA)')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Trošak', 'Cost')}</th>
                                <th style={{ textAlign: 'right' }}>{t('RUC', 'RUC')}</th>
                                <th style={{ textAlign: 'right' }}>{t('%', '%')}</th>
                                <th>{t('Status', 'Status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.offers.map((o) => {
                                const st = statusLabel(o.status);
                                return (
                                    <tr key={o.offer_id}>
                                        <td>
                                            <strong>{o.offer_number}</strong>
                                            <div className="muted" style={{ fontSize: '0.72rem' }}>{formatDate(o.offer_date, locale)}</div>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{formatMoney(o.offer_amount_eur, locale)}</td>
                                        <td style={{ textAlign: 'right', color: o.revenue_eur > 0 ? '#1f7a36' : '#999' }}>
                                            {o.revenue_eur > 0 ? formatMoney(o.revenue_eur, locale) : '—'}
                                            {o.linked_invoices_count > 0 ? <div className="muted" style={{ fontSize: '0.7rem' }}>{o.linked_invoices_count} IRA</div> : null}
                                        </td>
                                        <td style={{ textAlign: 'right', color: o.cost_eur > 0 ? '#b14040' : '#999' }}>
                                            {o.cost_eur > 0 ? formatMoney(o.cost_eur, locale) : '—'}
                                            {o.linked_supplier_orders_count > 0 ? <div className="muted" style={{ fontSize: '0.7rem' }}>{o.linked_supplier_orders_count} nar.</div> : null}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: o.ruc_eur >= 0 ? '#1f7a36' : '#b14040' }}>
                                            {formatMoney(o.ruc_eur, locale)}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: (o.ruc_pct ?? 0) >= 0 ? '#1f7a36' : '#b14040' }}>
                                            {o.ruc_pct !== null ? `${o.ruc_pct.toFixed(1)}%` : '—'}
                                        </td>
                                        <td>
                                            <span style={{ background: `${st.color}22`, color: st.color, padding: '2px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>
                                                {st.label}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
